// packages/web/src/components/OAEnrichmentDetail.tsx — misma lógica (getEnrichmentDetail, aprobar).
// Cambios: panel lateral en lugar de modal centrado, pestañas por dimensión Marzano (número + color),
// pesos de criterio como barra apilada con leyenda, acción "Aprobar" fija abajo, Escape cierra.
import { useEffect, useState } from 'react';
import { Loader2, X, ThumbsUp, CheckCircle2, BookOpen, Target, Wrench, Lightbulb, AlertTriangle } from 'lucide-react';
import { api, type EnrichmentDetail, type EnrichmentStatus } from '../api';
import { ALL_DIMS, DIM_LABEL, type Dim, type EvidenceCriteria } from '../types';
import { Alert, Button, StatusBadge } from './ui';

interface Props { enrichmentId: number; siteId: string; onClose: () => void; onApproved: () => void }

const STATUS: Record<EnrichmentStatus, { label: string; tone: 'ok' | 'warn' | 'muted' }> = {
  pending_curation: { label: 'Pendiente de curación', tone: 'warn' },
  approved: { label: 'Aprobado', tone: 'ok' },
  retired: { label: 'Retirado', tone: 'muted' },
};
const CRITERIA: Record<EvidenceCriteria, { label: string; bar: string }> = {
  precision: { label: 'Precisión', bar: 'bg-marzano-600' },
  profundidad: { label: 'Profundidad', bar: 'bg-marzano-400' },
  independencia: { label: 'Independencia', bar: 'bg-emerald-600' },
  justificacion: { label: 'Justificación', bar: 'bg-amber-500' },
};
const DIFFICULTY: Record<'low' | 'medium' | 'high', string> = { low: 'Baja', medium: 'Media', high: 'Alta' };
const DIM_STYLE: Record<Dim, { n: number; text: string; bar: string; soft: string }> = {
  recuperacion: { n: 1, text: 'text-[#2B5C9E]', bar: 'bg-[#2B5C9E]', soft: 'bg-[#E7EFFA]' },
  comprension: { n: 2, text: 'text-[#0B7A72]', bar: 'bg-[#0B7A72]', soft: 'bg-[#E0F4F1]' },
  analisis: { n: 3, text: 'text-[#6A47A6]', bar: 'bg-[#6A47A6]', soft: 'bg-[#EFE9F8]' },
  transferencia: { n: 4, text: 'text-[#A0530B]', bar: 'bg-[#A0530B]', soft: 'bg-[#FBEEDD]' },
};

export function OAEnrichmentDetail({ enrichmentId, siteId, onClose, onApproved }: Props) {
  const [detail, setDetail] = useState<EnrichmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [tab, setTab] = useState<'contexto' | Dim>('contexto');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getEnrichmentDetail(enrichmentId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enrichmentId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onApprove = async () => {
    if (!detail) return;
    setApproving(true);
    try { await api.approveEnrichment(siteId, detail.id); onApproved(); onClose(); }
    catch (e) { alert(e instanceof Error ? e.message : String(e)); }
    finally { setApproving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/50 flex justify-end" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="enr-title" onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-4xl h-full flex flex-col shadow-2xl">
        <header className="px-6 pt-5 pb-0 border-b border-slate-200 shrink-0 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-1.5">
              {detail && (
                <div className="flex items-center gap-2 flex-wrap text-[13px] text-slate-600">
                  <StatusBadge tone={STATUS[detail.status].tone}>{STATUS[detail.status].label}</StatusBadge>
                  {detail.oa_codigo && <span className="font-mono font-semibold text-marzano-700 bg-marzano-50 rounded px-1.5 py-0.5">{detail.oa_codigo}</span>}
                  <span>{[detail.asignatura, detail.nivel_academico, `v${detail.version_id}`, detail.tipo].filter(Boolean).join(' · ')}</span>
                </div>
              )}
              <h2 id="enr-title" className="text-lg font-semibold text-slate-900 leading-snug text-pretty">{detail?.aprendizaje_central ?? 'Matriz del OA'}</h2>
            </div>
            <button type="button" onClick={onClose} aria-label="Cerrar" className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-500">
              <X aria-hidden className="w-5 h-5" />
            </button>
          </div>
          {detail && (
            <div role="tablist" aria-label="Secciones" className="flex gap-1 overflow-x-auto -mb-px">
              <TabBtn active={tab === 'contexto'} onClick={() => setTab('contexto')}>Contexto</TabBtn>
              {ALL_DIMS.map((d) => (
                <TabBtn key={d} active={tab === d} onClick={() => setTab(d)}>
                  <span className={`inline-flex w-5 h-5 rounded-full items-center justify-center text-[11px] font-bold text-white ${DIM_STYLE[d].bar}`}>{DIM_STYLE[d].n}</span>
                  {DIM_LABEL[d]}
                </TabBtn>
              ))}
            </div>
          )}
        </header>

        <div className="overflow-y-auto flex-1 px-6 py-5">
          {loading && <p className="flex items-center justify-center gap-2 py-10 text-sm text-slate-700"><Loader2 aria-hidden className="w-4 h-4 animate-spin" />Cargando…</p>}
          {error && <Alert kind="error">{error}</Alert>}
          {detail && tab === 'contexto' && <Contexto detail={detail} />}
          {detail && tab !== 'contexto' && <DimensionPanel dim={tab} detail={detail} />}
        </div>

        {detail && (
          <footer className="flex items-center justify-between gap-3 px-6 py-3.5 border-t border-slate-200 bg-slate-50 shrink-0">
            <p className="text-xs text-slate-600">
              Generado con IA el {new Date(detail.generated_by_ai_at).toLocaleDateString('es-CL')}
              {detail.approved_at && <> · aprobado el {new Date(detail.approved_at).toLocaleDateString('es-CL')}</>}
            </p>
            {detail.status === 'pending_curation' ? (
              <Button onClick={() => void onApprove()} loading={approving} leftIcon={<ThumbsUp aria-hidden className="w-4 h-4" />}>Aprobar rúbrica</Button>
            ) : detail.status === 'approved' ? (
              <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-800"><CheckCircle2 aria-hidden className="w-4 h-4" />Aprobada · el motor la reutiliza</span>
            ) : <span className="text-[13px] text-slate-600">Retirada</span>}
          </footer>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" role="tab" aria-selected={active} onClick={onClick}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2.5 text-sm border-b-2 ${active ? 'border-marzano-600 font-semibold text-marzano-700' : 'border-transparent text-slate-600 hover:text-slate-900'} focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-500`}>
      {children}
    </button>
  );
}

function Contexto({ detail }: { detail: EnrichmentDetail }) {
  const buckets = [
    { title: 'Conceptos esenciales', Icon: BookOpen, items: detail.conceptos_esenciales },
    { title: 'Procedimientos esenciales', Icon: Wrench, items: detail.procedimientos_esenciales },
    { title: 'Habilidades', Icon: Lightbulb, items: detail.habilidades },
    { title: 'Indicadores de logro', Icon: Target, items: detail.indicadores_logro },
    { title: 'Errores frecuentes', Icon: AlertTriangle, items: detail.errores_frecuentes },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {buckets.map(({ title, Icon, items }) => (
        <section key={title} className="rounded-xl border border-slate-200 p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2">
            <Icon aria-hidden className="w-4 h-4 text-marzano-600" />{title}
            <span className="ml-auto text-xs font-normal text-slate-600 tabular-nums">{items.length}</span>
          </h3>
          {items.length > 0
            ? <ul className="space-y-1.5 text-sm text-slate-800 list-disc pl-4 marker:text-slate-400">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
            : <p className="text-[13px] text-slate-600">Sin registros.</p>}
        </section>
      ))}
    </div>
  );
}

function DimensionPanel({ dim, detail }: { dim: Dim; detail: EnrichmentDetail }) {
  const dd = detail.matrix?.dimensions?.[dim];
  const S = DIM_STYLE[dim];
  if (!dd) return <p className="text-sm text-slate-600 py-6 text-center">{DIM_LABEL[dim]} no tiene datos.</p>;
  const weights = Object.entries(dd.criteria_focus) as Array<[EvidenceCriteria, number]>;
  const total = weights.reduce((s, [, v]) => s + v, 0) || 1;

  return (
    <div className="space-y-5">
      <section aria-labelledby="w-t" className="space-y-2">
        <h3 id="w-t" className="text-sm font-semibold text-slate-900">Peso de cada criterio <span className="font-normal text-slate-600">· suman {total}</span></h3>
        <div className="flex h-3 rounded-full overflow-hidden" aria-hidden>
          {weights.map(([k, v]) => <span key={k} className={CRITERIA[k].bar} style={{ width: `${(v / total) * 100}%` }} />)}
        </div>
        <ul className="flex flex-wrap gap-x-5 gap-y-1 text-[13px]">
          {weights.map(([k, v]) => (
            <li key={k} className="inline-flex items-center gap-1.5"><span aria-hidden className={`w-2.5 h-2.5 rounded-sm ${CRITERIA[k].bar}`} /><span className="text-slate-700">{CRITERIA[k].label}</span><strong className="tabular-nums">{v}%</strong></li>
          ))}
        </ul>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-5">
        <section className={`rounded-xl p-4 ${S.soft}`}>
          <h3 className={`text-sm font-semibold mb-2 ${S.text}`}>Evidencias esperadas · {dd.evidencias_esperadas.length}</h3>
          {dd.evidencias_esperadas.length > 0
            ? <ul className="space-y-1.5 text-sm text-slate-900 list-disc pl-4">{dd.evidencias_esperadas.map((e, i) => <li key={i}>{e}</li>)}</ul>
            : <p className="text-[13px] text-slate-700">—</p>}
        </section>
        <section>
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Banco de preguntas · {dd.banco_preguntas.length}</h3>
          {dd.banco_preguntas.length > 0 ? (
            <ol className="space-y-2.5">
              {dd.banco_preguntas.map((q, i) => (
                <li key={q.id ?? i} className="rounded-xl border border-slate-200 p-3.5 space-y-1.5">
                  <div className="flex items-center gap-2 text-xs">
                    <StatusBadge tone={q.source === 'curated' ? 'ok' : 'info'}>{q.source === 'curated' ? 'Curada' : 'Generada'}</StatusBadge>
                    <span className="text-slate-600">Dificultad {DIFFICULTY[q.difficulty].toLowerCase()}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-900">{q.text}</p>
                  {q.expected_evidence && <p className="text-[13px] text-slate-700"><span className="font-semibold">Evidencia esperada:</span> {q.expected_evidence}</p>}
                </li>
              ))}
            </ol>
          ) : <p className="text-[13px] text-slate-600">—</p>}
        </section>
      </div>
    </div>
  );
}
